// tslint:disable
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
    MicroViewRenderChannel,
    MicroViewDisposeChannel,
    MicroViewBundleReadyChannel,
    MicroViewPropsChangeChannel,
    MicroViewFirstTimeRenderChannel
} from '@tableau/microview-communication-service/lib/src/channelDefinitions/MicroViewLifecyleChannels';
import { MicroviewMetadata } from './MicroviewMetadata';
import { Messages } from '../gen-src/Localize';

export function startMicroview<T>(microviewMetadata: MicroviewMetadata, AppComponent: (props: T) => React.ReactElement) {

    const microviewName = microviewMetadata.name;

    (new MicroViewRenderChannel(microviewName)).listen((props) => {
        initialize(props as T);
    });

    (new MicroViewDisposeChannel(microviewName)).listen(() => {
        const hostElement = getMicroViewHostElement();
        if (hostElement) {
            // We reluctantly take this as a temporary fix.
            // The following story will track work to remove this entirely. Bottom line, we have a leak and need to hunt it down.
            // Story 1347295: [FE] [Techdebt] The microview must unload cleanly when unmounted from the dom
            ReactDOM.unmountComponentAtNode(hostElement);

            // stopListeningToMicroViewRenderChannel();
            // stopListeningToMicroViewDisposeChannel();

        }
    });

    new MicroViewBundleReadyChannel(microviewName).call(); // It tells the microview host the microview bundle code is ready to work.

    function initialize(props: T) {
        if (getMicroViewHostElement()) {
            waitForLocalizationJSDownloaded().then(() => {
                render(props);
            }, () => {
                renderError(Messages.searchMicroviewErrorMessage());
            });
        } else {
            window.onload = function () {
                waitForLocalizationJSDownloaded().then(() => {
                    render(props);
                }, () => {
                    renderError(Messages.searchMicroviewErrorMessage());
                });
            };
        }
    }

    function render(props: T) {
        const hostElement = getMicroViewHostElement();
        if (hostElement) {
            ReactDOM.render(<ConnectedComponent props={props} />, hostElement);
        }
    }

    function renderError(errorMessage: string) {
        const hostElement = getMicroViewHostElement();
        if (hostElement) {
            ReactDOM.render(<div>{errorMessage}</div>, hostElement);
        }
        new MicroViewFirstTimeRenderChannel(microviewName).call();
    }

    /* Connected Component to wrap the app component around with props */
    type ConnectedComponent = {
        componentProps: T;
        children: React.ReactNode;
    }
    const ConnectedComponent = (componentProps: { props: T; }) => {
        const [propsChange, setProps] = React.useState<T>(componentProps.props);

        React.useEffect(() => {
            let stopListeningToMicroViewPropsChangeChannel: () => void;
            stopListeningToMicroViewPropsChangeChannel = new MicroViewPropsChangeChannel(microviewName).listen((p) => {
                setProps(p as T);
            });
            return () => { stopListeningToMicroViewPropsChangeChannel(); }
        }, [propsChange]);

        React.useEffect(() => {
            try {
                new MicroViewFirstTimeRenderChannel(microviewName).call();
            }
            // eslint-disable-next-line no-empty
            catch (e) { }
        }, []);

        return <AppComponent {...propsChange} />
    }

    function getMicroViewHostElement() {
        return document.getElementById(microviewName);
    }

    // The following function is a temporary implementation for sync up with localization JS initialization.
    // After communication service is implemented, the following function will be removed.
    // Instead, we will use communication service to tell the micro view that the localization JS initialization is done.
    function waitForLocalizationJSDownloaded(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let retryCount = 0;
            const handler = () => {
                if (!(window as any)['LocalizeNamespace-' + microviewMetadata.name]?.Localize?.msg?.formatMessage) {
                    if (retryCount > 20) {
                        reject('Exceeds the max amount of retry attempt to wait for the localization JS to be downloaded.');
                    }
                    retryCount++;
                    setTimeout(() => {
                        handler();
                    }, 100);
                } else {
                    resolve();
                }
            };
            handler();
        });
    }
}
